<script setup lang="ts">
interface ImportantAnnouncementItem {
  id: string;
  title: string;
  slug: string;
  uri?: string;
  type: 'Academic' | 'Event';
  eventDateStart?: string | null;
  eventDescription?: string | null;
}

interface ImportantAnnouncementsModule {
  importantAnnouncements: boolean;
  importantAnnouncementsItems: ImportantAnnouncementItem[];
}

const props = withDefaults(
  defineProps<{
    module?: ImportantAnnouncementsModule | null;
    heading?: string;
    defaultOpen?: boolean;
  }>(),
  {
    module: null,
    heading: 'Important Announcements',
    defaultOpen: true,
  },
);

const isOpen = ref(props.defaultOpen);

const parseCMSDate = (dateStr?: string | null) => {
  if (!dateStr) return null;

  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [_, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
};

const formatAnnouncementDate = (dateStr?: string | null) => {
  const d = parseCMSDate(dateStr);
  if (!d) return '';

  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const today = useToday();

const isElapsedAnnouncement = (dateStr?: string | null) => {
  const date = parseCMSDate(dateStr);
  if (!date) return false;

  return date.getTime() < today.value.getTime();
};

const items = computed(() => {
  if (!props.module?.importantAnnouncements) return [];

  return [...(props.module.importantAnnouncementsItems ?? [])]
    .filter((item) => !isElapsedAnnouncement(item.eventDateStart))
    .sort((a, b) => {
      const aDate = parseCMSDate(a.eventDateStart)?.getTime();
      const bDate = parseCMSDate(b.eventDateStart)?.getTime();

      if (aDate == null && bDate == null) return 0;
      if (aDate == null) return 1;
      if (bDate == null) return -1;

      return aDate - bDate;
    })
    .slice(0, 4);
});

const hasItems = computed(() => items.value.length > 0);
</script>

<template>
  <section
    v-if="hasItems"
    class="border-brand-700 bg-brand-50 mb-10 w-full rounded-2xl border px-4 py-4 md:px-8 md:py-6"
  >
    <button
      type="button"
      class="flex w-full cursor-pointer items-center justify-between gap-3 text-left"
      @click="isOpen = !isOpen"
      :aria-expanded="isOpen"
      :aria-label="heading"
    >
      <TypographyTitle as="h3" size="sm">{{ heading }}</TypographyTitle>
      <span class="text-brand-600 inline-flex items-center" aria-hidden="true">
        <svg
          :class="['h-5 w-5 transition-transform duration-200', isOpen ? 'rotate-180' : 'rotate-0']"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    </button>

    <div v-if="isOpen" class="mt-4 flex flex-col gap-3 md:gap-4">
      <div
        v-for="item in items"
        :key="item.id"
        class="flex flex-col items-start gap-2 rounded-lg p-2 md:flex-row md:items-start md:gap-3"
      >
        <span
          :class="[
            'mt-0.5 inline-flex min-w-[92px] justify-center rounded px-2 py-1 text-xs font-bold text-white',
            item.type === 'Academic' ? 'bg-brand-700' : 'bg-brand-500',
          ]"
        >
          {{ item.type }}
        </span>

        <div class="flex flex-col gap-1">
          <div class="text-brand-600 text-sm font-bold md:text-base">{{ item.title }}</div>

          <div
            v-if="formatAnnouncementDate(item.eventDateStart)"
            class="text-brand-600 text-xs font-semibold md:text-sm"
          >
            {{ formatAnnouncementDate(item.eventDateStart) }}
          </div>

          <div
            v-if="item.type !== 'Academic' && item.eventDescription"
            :key="`desc-${item.id}`"
            class="text-xs text-black/80 md:text-sm"
            v-html="item.eventDescription"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
:deep(p) {
  margin: 0;
}
</style>
